import {
  getConversationById,
  getConversationsForPage,
  getMessageForConversation,
} from "@/api/fbapi";
import { toast } from "@/hooks/use-toast";
import { getStoredAuth } from "@/lib/auth-utils";
import {
  handleFacebookReconnection,
  isOAuthExpirationError,
} from "@/lib/oauth-error-utils";
import { mapWebsocketEventToFbMessage, mergeChatAttachments, resolveAttachmentUrl } from "@/services/chat-message-normalizer";
import { getPageId } from "@/services/utl-service";
import { FbConversation, FBMessage } from "@/types/fb-type";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";

type ChatContextType = {
  onMessage: (body: any) => void;
  messages: FBMessage[];
  conversations: FbConversation[];
  loadingConversations: boolean;
  isLoadingMoreConversations: boolean;
  hasMoreConversations: boolean;
  onLoadMoreConversations: () => void;
  currentConversation: FbConversation | null;
  selectConversationById: (conversationId: string) => void;
  selectConversation: (conversation: FbConversation | null) => void;
  sendMessage: (message: FBMessage) => void;
  isLoadingMessage: boolean;
  isLoadingMore: boolean;
  onLoadMoreMessage: (prevCursor: string) => void;
  messageAfterCursor: string | null;
  hasMoreMessages: boolean;
  setOnLoadedMessages?: (
    cb: (msgs: FBMessage[], conv: FbConversation | null) => void
  ) => void;
  reloadMessages: (conv: FbConversation) => void;
  /** Lightweight refresh: re-fetches messages for the current conversation without
   *  resetting the currentConversation state (avoids the double-fetch side-effect).
   *  Returns a Promise that resolves when the fetch completes. */
  refreshMessages: (refreshMedia?: boolean, dedupeMediaRefresh?: boolean) => Promise<void>;
  reloadConversationMessages: (conversationId: string) => Promise<void>;

  oauthError: boolean;
  reconnectFacebook: () => Promise<void>;
  refetchConversation?: () => void;
  updateConversationAssignment?: (customerFbId: string, username: string) => void;
  /**
   * Called by ChatPage (via useWebSocketChannel) when a raw STOMP message
   * arrives on the page-level topic /topic/conversations/{pageId}.
   * Returns the human sender id when the message was for a non-active
   * conversation (so ChatPage can show a toast), or null when it was routed
   * into the currently open window.
   */
  handleGlobalIncomingMessage: (rawBody: string) => string | null;
};

function attachmentPreviewUrl(attachment: any): string {
  return (
    attachment?.imageData?.url ||
    attachment?.imageData?.previewUrl ||
    attachment?.fileUrl ||
    attachment?.url ||
    ''
  );
}

function revokeMessageBlobUrls(messages: FBMessage[]) {
  if (typeof URL === 'undefined') return;
  for (const message of messages) {
    for (const attachment of message.attachments ?? []) {
      const url = attachmentPreviewUrl(attachment);
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    }
  }
}

function mergeMessageList(oldMsgs: FBMessage[], newMsgs: FBMessage[]): FBMessage[] {
  const map = new Map<string, FBMessage>();

  // 1. Seed with old messages
  for (const oldMsg of oldMsgs) {
    if (oldMsg && oldMsg.id) {
      map.set(oldMsg.id, oldMsg);
    }
  }

  // 2. Merge new messages
  for (const newMsg of newMsgs) {
    if (!newMsg || !newMsg.id) continue;

    // If newMsg is an authoritative message from server, reconcile any matching optimistic temp-* message
    if (!newMsg.id.startsWith('temp-')) {
      for (const [key, existingTemp] of map.entries()) {
        if (key.startsWith('temp-')) {
          const oldTime =
            typeof existingTemp.getCreatedAt === 'function'
              ? existingTemp.getCreatedAt().getTime()
              : new Date((existingTemp as any).createdTime || 0).getTime();
          const newTime =
            typeof newMsg.getCreatedAt === 'function'
              ? newMsg.getCreatedAt().getTime()
              : new Date((newMsg as any).createdTime || 0).getTime();
          const timeDiff = Math.abs(oldTime - newTime);

          const sameType =
            (existingTemp.attachments?.[0]?.image && newMsg.attachments?.[0]?.image) ||
            (existingTemp.attachments?.[0]?.audio && newMsg.attachments?.[0]?.audio) ||
            (existingTemp.message && newMsg.message && existingTemp.message === newMsg.message);

          if (timeDiff < 90000 && sameType) {
            // Transfer optimistic preview blob if server hasn't finished indexing CDN URL
            const oldAtt = existingTemp.attachments?.[0];
            const oldUrl = oldAtt?.imageData?.url || oldAtt?.fileUrl;
            const newAtt = newMsg.attachments?.[0];
            const newUrl = newAtt?.imageData?.url || newAtt?.fileUrl;
            if (oldUrl?.startsWith('blob:') && (!newUrl || newUrl.startsWith('blob:'))) {
              newMsg.attachments = existingTemp.attachments;
            }
            map.delete(key);
            break;
          }
        }
      }
    }

    const existing = map.get(newMsg.id);
    if (!existing) {
      map.set(newMsg.id, newMsg);
    } else {
      // API results and webhook replays can omit media while Facebook indexes it.
      const mergedAttachments = mergeChatAttachments(existing.attachments ?? [], newMsg.attachments ?? []);

      const merged = Object.assign(new FBMessage(), existing, newMsg, {
        attachments: mergedAttachments,
        sticker: newMsg.sticker || existing.sticker || '',
        from: newMsg.from?.id ? newMsg.from : existing.from?.id ? existing.from : newMsg.from,
      });

      // Once Facebook supplies a durable URL, release the local preview URL that
      // was transferred from ChatInput to this message.
      for (const oldAttachment of existing.attachments ?? []) {
        const oldUrl = attachmentPreviewUrl(oldAttachment);
        if (!oldUrl.startsWith('blob:')) continue;
        const replacementUrl = (newMsg.attachments ?? [])
          .map(attachmentPreviewUrl)
          .find((url) => url && !url.startsWith('blob:'));
        if (replacementUrl && typeof URL !== 'undefined') URL.revokeObjectURL(oldUrl);
      }
      map.set(newMsg.id, merged);
    }
  }

  const mergedList = Array.from(map.values());
  mergedList.sort((a, b) => {
    const timeA = typeof a.getCreatedAt === 'function' ? a.getCreatedAt().getTime() : new Date((a as any).createdTime || 0).getTime();
    const timeB = typeof b.getCreatedAt === 'function' ? b.getCreatedAt().getTime() : new Date((b as any).createdTime || 0).getTime();
    return timeA - timeB;
  });

  return mergedList;
}

export const ChatContext = createContext<ChatContextType | undefined>(
  undefined
);
export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { accessToken } = getStoredAuth();
  const [messages, setMessages] = useState<FBMessage[]>([]);
  const [conversations, setConversations] = useState<FbConversation[]>([]);
  const [loadingConversations, setLoadingConversations] =
    useState<boolean>(false);
  const [currentConversation, setCurrentConversation] =
    useState<FbConversation | null>(null);
  const [isLoadingMessage, setLoadingMessage] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [messageAfterCursor, setMessageAfterCursor] = useState<string | null>(
    null
  );
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(false);
  const [oauthError, setOauthError] = useState<boolean>(false);
  /** Conversation list pagination */
  const [conversationNextCursor, setConversationNextCursor] = useState<string | null>(null);
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] = useState<boolean>(false);
  const hasMoreConversations = conversationNextCursor !== null;
  /** Prevents concurrent "load more" fetches */
  const inFlightConvCursorRef = useRef<string | 'first' | null>(null);
  const convoAbortRef = useRef<AbortController | null>(null);
  const msgAbortRef = useRef<AbortController | null>(null);
  /** Monotonic counter so only the latest message fetch may apply results */
  const fetchSeqRef = useRef(0);
  const conversationLoadSeqRef = useRef(0);
  const paginationSeqRef = useRef(0);
  const { isAuthenticated } = useAuth();
  const messagesRef = useRef<FBMessage[]>(messages);
  messagesRef.current = messages;
  const onLoadedMessagesRef = useRef<
    ((msgs: FBMessage[], conv: FbConversation | null) => void) | null
  >(null);
  const inFlightLoadCursorRef = useRef<string | null>(null);

  const clearMessages = useCallback(() => {
    revokeMessageBlobUrls(messagesRef.current);
    messagesRef.current = [];
    setMessages([]);
  }, []);

  /** Stable ref to the current conversation — lets the WS callback read the
   *  latest value without being recreated every time it changes. */
  const currentConversationRef = useRef<FbConversation | null>(null);
  const mediaRefreshRef = useRef<{ conversationId: string; requestedAt: number } | null>(null);
  useEffect(() => {
    currentConversationRef.current = currentConversation;
  }, [currentConversation]);

  const onMessage = React.useCallback((body: any) => {
    setMessages((prev) => mergeMessageList(prev, [body]));

    const pageId = getPageId();
    const senderId =
      body?.sender?.id ??
      body?.from?.id ??
      '';
    const activConv = currentConversationRef.current;
    if (activConv && senderId && senderId !== pageId) {
      const activeUser = activConv.getUser();
      if (!activeUser?.id || activeUser.id === senderId) {
        const updated = Object.assign(
          Object.create(Object.getPrototypeOf(activConv)),
          activConv,
          {
            snippet: body?.message || activConv.snippet,
            updatedTime: body?.createdTime || new Date().toISOString(),
            lastCustomerMessageTime: body?.createdTime || new Date().toISOString(),
            messagingWindow: 'STANDARD' as const,
          },
        );
        setCurrentConversation(updated);
        setConversations((prev) =>
          prev.map((conversation) => (conversation.id === updated.id ? updated : conversation)),
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setConversations([]);
      setCurrentConversation(null);
      clearMessages();
      setMessageAfterCursor(null);
      setIsLoadingMore(false);
      inFlightLoadCursorRef.current = null;
      setOauthError(false);
      // cancel any in-flight fetches
      convoAbortRef.current?.abort();
      msgAbortRef.current?.abort();
    }
  }, [clearMessages, isAuthenticated]);

  const setOnLoadedMessages = useCallback(
    (cb: (msgs: FBMessage[], conv: FbConversation | null) => void) => {
      onLoadedMessagesRef.current = cb;
    },
    []
  );
  const fetchConversations = async (after?: string) => {
    if (after) {
      // "load more" — guard against concurrent fetches for the same cursor
      if (inFlightConvCursorRef.current === after) return;
      inFlightConvCursorRef.current = after;
      setIsLoadingMoreConversations(true);
    } else {
      // initial / refresh fetch
      if (inFlightConvCursorRef.current === 'first') return;
      inFlightConvCursorRef.current = 'first';
      setLoadingConversations(true);
      setOauthError(false);
    }
    try {
      const conversationResponse = await getConversationsForPage(after);
      if (conversationResponse.success) {
        const page = conversationResponse.data;
        const conv = page?.data ?? [];
        setConversationNextCursor(page?.nextCursor ?? null);
        if (after) {
          // Append to existing list, dedupe by id
          setConversations((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const fresh = conv.filter((c) => !existingIds.has(c.id));
            return [...prev, ...fresh];
          });
        } else {
          setConversations(conv);
        }
      } else {
        // Check if it's an OAuth expiration error
        // The error message might be in the response message
        const errorToCheck = {
          message: conversationResponse.message,
          response: conversationResponse,
        };
        if (isOAuthExpirationError(errorToCheck)) {
          setOauthError(true);
          // Don't show generic error toast, let the UI component handle it
        } else {
          toast({
            title: "Error",
            description: conversationResponse.message,
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      // Check if it's an OAuth expiration error
      if (isOAuthExpirationError(error)) {
        setOauthError(true);
        // Don't show generic error toast, let the UI component handle it
      } else {
        toast({
          title: "Error",
          description: error?.message || "Failed to load conversations",
          variant: "destructive",
        });
      }
    } finally {
      inFlightConvCursorRef.current = null;
      if (after) {
        setIsLoadingMoreConversations(false);
      } else {
        setLoadingConversations(false);
      }
    }
  };

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    convoAbortRef.current?.abort();
    convoAbortRef.current = new AbortController();

    fetchConversations();
    return () => {
      convoAbortRef.current?.abort();
    };
  }, [accessToken]);

  const fetchMessages = async (
    conversationId?: string,
    { refreshMedia = false, silent = false, refreshHistory = false, cacheOnly = false }: {
      refreshMedia?: boolean;
      silent?: boolean;
      refreshHistory?: boolean;
      cacheOnly?: boolean;
    } = {},
  ) => {
    const activeId = conversationId || currentConversationRef.current?.id;
    if (!activeId) {
      return;
    }
    const isStale = () => currentConversationRef.current?.id !== activeId;
    // Stale refresh for a conversation that is no longer open — ignore entirely
    if (isStale()) return;
    const requestSeq = ++fetchSeqRef.current;
    const paginationSeq = paginationSeqRef.current;
    // Silent (background) refreshes must not flash the loading state or reset paging.
    if (!silent) {
      setLoadingMessage(true);
      setIsLoadingMore(false);
      inFlightLoadCursorRef.current = null;
    }
    try {
      const messageResponse = await getMessageForConversation(
        activeId,
        undefined,
        refreshMedia,
        refreshHistory,
        cacheOnly,
      );
      // Discard responses for a conversation the user already navigated away from
      if (isStale() || requestSeq !== fetchSeqRef.current) return;
      if (messageResponse.success) {
        const incoming = messageResponse.data.data;
        setMessages((prev) => {
          const sorted = mergeMessageList(prev, incoming);
          onLoadedMessagesRef.current?.(sorted, currentConversationRef.current);
          return sorted;
        });
        if ((!silent || refreshHistory || cacheOnly) && paginationSeq === paginationSeqRef.current) {
          setMessageAfterCursor(messageResponse.data.nextCursor);
          setHasMoreMessages(!!messageResponse.data.nextCursor);
        }
        // Missing media is fetched from Facebook by the backend in the background; it then
        // sends a MEDIA_READY event and the client reloads silently (see reloadConversationMessages).
        return incoming.length > 0;
      }
    } catch {
      // swallow — UI already shows loading state
    } finally {
      // Only the latest request for the active conversation controls the loading flag
      if (!silent && requestSeq === fetchSeqRef.current && !isStale()) {
        setLoadingMessage(false);
      }
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    const loadSeq = ++conversationLoadSeqRef.current;
    const isStale = () => currentConversationRef.current?.id !== conversationId
      || loadSeq !== conversationLoadSeqRef.current;
    if (isStale()) return;
    setLoadingMessage(true);
    setIsLoadingMore(false);
    inFlightLoadCursorRef.current = null;
    try {
      // This request never waits for Facebook, even for a partial cache or missing cursor.
      const hasCachedMessages = await fetchMessages(conversationId, { cacheOnly: true, silent: true });
      if (isStale()) return;
      if (hasCachedMessages) setLoadingMessage(false);
      // Show cached history while Graph supplies Business Suite replies and a fresh cursor.
      await fetchMessages(conversationId, { refreshHistory: true, silent: true });
    } finally {
      if (!isStale()) setLoadingMessage(false);
    }
  };

  useEffect(() => {
    if (!accessToken || !currentConversation?.id) return;
    clearMessages();
    setIsLoadingMore(false);
    inFlightLoadCursorRef.current = null;
    void loadConversationMessages(currentConversation.id);
    msgAbortRef.current?.abort();
    msgAbortRef.current = new AbortController();

    return () => {
      msgAbortRef.current?.abort();
    };
  }, [accessToken, clearMessages, currentConversation?.id]);

  const sendMessage = async (message: FBMessage) => {
    setMessages((prev) => mergeMessageList(prev, [message]));
  };
  const selectConversationById = async (id: string) => {
    let conv: FbConversation | null = conversations.find((c) => c.id === id) || null;
    if (!conv) {
      setLoadingMessage(true);
      try {
        const response = await getConversationById(id);

        if (response.success) {
          conv = response.data;
          setConversations((prev) => {
            return conv ? [conv, ...prev] : prev;
          });
          setCurrentConversation(conv ?? null);
        }
        else {
          toast({
            title: "Error",
            description: response.message || "Error fetching conversation ",
            variant: "destructive",
          });
          conv = null;
          setCurrentConversation(null);
        }

      } catch {
        toast({
          title: "Error",
          description: "Error fetching conversation ",
          variant: "destructive",
        });
        conv = null;
        setCurrentConversation(null);
      }
      finally {
        setLoadingMessage(false);
      }
    } else {
      setCurrentConversation(conv);
    }
    if (conv && currentConversationRef.current?.id === conv.id) {
      void loadConversationMessages(conv.id);
    }
  };

  const selectConversation = useCallback((conv: FbConversation | null) => {
    setCurrentConversation(conv);
    clearMessages();
    setMessageAfterCursor(null);
    setHasMoreMessages(false);
    setIsLoadingMore(false);
    inFlightLoadCursorRef.current = null;
    if (conv && currentConversationRef.current?.id === conv.id) {
      void loadConversationMessages(conv.id);
    }
  }, []);

  const onLoadMoreMessage = useCallback(async (prevCursor: string) => {
    if (!currentConversation || !prevCursor) return;
    if (inFlightLoadCursorRef.current === prevCursor) return;
    ++paginationSeqRef.current;
    inFlightLoadCursorRef.current = prevCursor;
    setIsLoadingMore(true);

    try {
      const messageResponse = await getMessageForConversation(
        currentConversation.id,
        prevCursor
      );
      if (currentConversationRef.current?.id !== currentConversation.id) return;
      if (messageResponse.success) {
        setMessages((prev) => mergeMessageList(prev, messageResponse.data.data));
        setMessageAfterCursor(messageResponse.data.nextCursor);
        setHasMoreMessages(!!messageResponse.data.nextCursor);
      }
    } catch {
      // swallow – caller handles UI feedback
    } finally {
      setIsLoadingMore(false);
      if (inFlightLoadCursorRef.current === prevCursor) {
        inFlightLoadCursorRef.current = null;
      }
    }
  }, [clearMessages, currentConversation]);

  const reloadMessages = (conversation: FbConversation) => {
    setCurrentConversation(conversation);
    void loadConversationMessages(conversation.id);
  };

  /** Re-fetches messages for the currently open conversation without touching
   *  currentConversation state — use this after sending attachments/voice. */
  const refreshMessages = useCallback((refreshMedia = false, dedupeMediaRefresh = false): Promise<void> => {
    if (currentConversation) {
      if (refreshMedia) {
        const now = Date.now();
        const previous = mediaRefreshRef.current;
        // Several broken photos in one history page should trigger only one Facebook reload.
        if (dedupeMediaRefresh && previous?.conversationId === currentConversation.id && now - previous.requestedAt < 10000) {
          return Promise.resolve();
        }
        mediaRefreshRef.current = { conversationId: currentConversation.id, requestedAt: now };
      }
      return fetchMessages(currentConversation.id, { refreshMedia, silent: refreshMedia }).then(() => {});
    }
    return Promise.resolve();
  }, [currentConversation]);

  /** Called when the backend reports fresh media for a conversation. Reads the (now updated)
   *  server cache silently; ignored when that conversation is no longer the open one. */
  const reloadConversationMessages = useCallback((conversationId: string): Promise<void> => {
    if (!conversationId || currentConversationRef.current?.id !== conversationId) {
      return Promise.resolve();
    }
    return fetchMessages(conversationId, { silent: true }).then(() => {});
  }, []);


  const reconnectFacebook = useCallback(async () => {
    await handleFacebookReconnection();
    // Clear OAuth error state after reconnection attempt
    setOauthError(false);
  }, []);

  const refetchConversation = async () => {
    setConversationNextCursor(null); // reset pagination on explicit refresh
    fetchConversations();
  };

  const updateConversationAssignment = useCallback((customerFbId: string, username: string) => {
    if (!customerFbId || !username) return;
    setConversations((prev) =>
      prev.map((c) => {
        if (c.getUser()?.id !== customerFbId) return c;
        const existingUsers = c.assignedUsers ?? [];
        const assignedUsers = existingUsers.some(
          (u) => u && u.toLowerCase() === username.toLowerCase(),
        )
          ? existingUsers
          : [username, ...existingUsers];
        return Object.assign(new FbConversation(), c, {
          followUpUser: username,
          assignedUsers,
        });
      }),
    );
    setCurrentConversation((prev) => {
      if (!prev || prev.getUser()?.id !== customerFbId) return prev;
      const existingUsers = prev.assignedUsers ?? [];
      const assignedUsers = existingUsers.some(
        (u) => u && u.toLowerCase() === username.toLowerCase(),
      )
        ? existingUsers
        : [username, ...existingUsers];
      return Object.assign(new FbConversation(), prev, {
        followUpUser: username,
        assignedUsers,
      });
    });
  }, []);

  const onLoadMoreConversations = useCallback(() => {
    if (!conversationNextCursor || isLoadingMoreConversations) return;
    fetchConversations(conversationNextCursor);
  }, [conversationNextCursor, isLoadingMoreConversations]);

  /**
   * Stable ref to fetchConversations so handleGlobalIncomingMessage can
   * trigger a reload without taking fetchConversations as a useCallback dep
   * (which would recreate the handler on every render).
   */
  const fetchConversationsRef = useRef(fetchConversations);
  useEffect(() => {
    fetchConversationsRef.current = fetchConversations;
  });

  /**
   * Processes a raw STOMP message body from the page-level topic
   * /topic/conversations/{pageId}.
   *
   * - If the message belongs to the currently open conversation it is routed
   *   directly into the message list (same as a per-conversation event).
   * - If it belongs to a different conversation the sidebar entry is updated
   *   (snippet + unreadCount) and the human sender id is returned so the
   *   caller (ChatPage) can surface a toast.
   *
   * Returns the human sender id for non-active conversations, or null when
   * the message was for the active conversation (no toast needed).
   */
  const handleGlobalIncomingMessage = useCallback((rawBody: string): string | null => {
    const pageId = getPageId();
    try {
      const fbMessage = mapWebsocketEventToFbMessage(rawBody);
      if (!fbMessage) return null;

      // Identify the human participant (the side that is NOT the page)
      const senderId =
        (fbMessage as any).sender?.id ??
        (fbMessage as any).from?.id ??
        fbMessage.from?.id ??
        '';
      const recipientId =
        (fbMessage as any).recipient?.id ??
        (fbMessage.to?.[0] as any)?.id ??
        '';
      const humanId = senderId === pageId ? recipientId : senderId;

      const activConv = currentConversationRef.current;

      // Route into the currently open conversation window — no toast
      if (activConv) {
        const activeUser = activConv.getUser();
        if (activeUser?.id === humanId || activeUser?.id === senderId) {
          onMessage(fbMessage);
          if (senderId !== pageId) {
            const updated = Object.assign(
              Object.create(Object.getPrototypeOf(activConv)),
              activConv,
              {
                snippet: fbMessage.message || activConv.snippet,
                updatedTime: fbMessage.createdTime || new Date().toISOString(),
                lastCustomerMessageTime: fbMessage.createdTime || new Date().toISOString(),
                messagingWindow: 'STANDARD' as const,
              },
            );
            setCurrentConversation(updated);
            setConversations((prev) => prev.map((conversation) =>
              conversation.id === updated.id ? updated : conversation,
            ));
          }
          return null;
        }
      }

      // ── Different (or no) conversation ──────────────────────────────────
      const snippet =
        typeof fbMessage.message === 'string' && fbMessage.message.trim()
          ? fbMessage.message.trim()
          : fbMessage.sticker
            ? '🎭 Sticker'
            : (fbMessage.attachments?.length ?? 0) > 0
              ? '📷 Image'
              : '';

      setConversations((prev) => {
        const idx = prev.findIndex((c) => {
          const u = c.getUser();
          return u?.id === humanId || u?.id === senderId;
        });

        if (idx === -1) {
          // User not in the list yet — trigger a background fetch so the
          // new conversation is loaded and prepended by the server response.
          void fetchConversationsRef.current();
          return prev; // leave list unchanged until the fetch resolves
        }

        // Build updated copy of the matched entry
        const updated = Object.assign(
          Object.create(Object.getPrototypeOf(prev[idx])),
          prev[idx],
          {
            snippet,
            updatedTime: fbMessage.createdTime || new Date().toISOString(),
            unreadCount: (prev[idx].unreadCount ?? 0) + 1,
            ...(senderId && senderId !== pageId
              ? {
                  lastCustomerMessageTime: fbMessage.createdTime || new Date().toISOString(),
                  messagingWindow: 'STANDARD' as const,
                }
              : {}),
          }
        );

        // Move to the top: remove from current position, prepend
        const rest = prev.filter((_, i) => i !== idx);
        return [updated, ...rest];
      });

      // Return humanId so ChatPage can show a toast
      return humanId || senderId || null;
    } catch (err) {
      console.warn('[ChatContext] handleGlobalIncomingMessage error', err);
      return null;
    }
  }, [onMessage]);


  const value = useMemo<ChatContextType>(
    () => ({
      onMessage,
      messages,
      conversations,
      loadingConversations,
      isLoadingMoreConversations,
      hasMoreConversations,
      onLoadMoreConversations,
      currentConversation,
      selectConversationById,
      selectConversation,
      sendMessage,
      isLoadingMessage,
      isLoadingMore,
      onLoadMoreMessage,
      messageAfterCursor,
      hasMoreMessages,
      setOnLoadedMessages,
      reloadMessages,
      refreshMessages,
      reloadConversationMessages,
      oauthError,
      reconnectFacebook,
      refetchConversation,
      updateConversationAssignment,
      handleGlobalIncomingMessage,
    }),
    [
      onMessage,
      messages,
      conversations,
      loadingConversations,
      isLoadingMoreConversations,
      hasMoreConversations,
      onLoadMoreConversations,
      currentConversation,
      selectConversationById,
      selectConversation,
      sendMessage,
      isLoadingMessage,
      isLoadingMore,
      onLoadMoreMessage,
      messageAfterCursor,
      hasMoreMessages,
      setOnLoadedMessages,
      reloadMessages,
      refreshMessages,
      reloadConversationMessages,
      oauthError,
      reconnectFacebook,
      refetchConversation,
      updateConversationAssignment,
      handleGlobalIncomingMessage,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
